/**
 * Watch Live — the link that was missing from the church's own page.
 *
 * The church broadcasts, and the stream page existed, but on `/church/:slug`
 * there was nothing pointing at it: a member who went to their church's page
 * on a Sunday morning had no way of reaching the service they were looking
 * for. The link now sits with the other things a visitor does.
 *
 * It says whether a service is on air rather than claiming one always is: the
 * same honest signal the broadcast desk sees, so "LIVE NOW" only appears when
 * video is genuinely arriving.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlayCircle, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const WatchLiveLink = ({ slug, className = '', testId = 'watch-live-link' }) => {
  const { data } = useQuery({
    queryKey: ['church-public-live', slug],
    queryFn: () => fetch(`${API}/api/church/public/by-slug/${slug}/live`)
      .then((r) => (r.ok ? r.json() : null)),
    enabled: !!slug,
    refetchInterval: 60000,
    retry: false,
  });

  if (!slug) return null;
  const live = !!data?.live;
  const next = data?.next;
  // Nothing on air and nothing scheduled: a dead link to an empty page helps
  // nobody, so the link only appears when there is something to watch.
  if (!live && !next && !data?.last) return null;

  return (
    <Link
      to={`/churches/${slug}/live`}
      className={className}
      data-testid={testId}
      aria-label={live ? 'Watch the service live now' : 'Watch services'}
    >
      {live
        ? <Radio className="h-4 w-4 animate-pulse text-red-400" />
        : <PlayCircle className="h-4 w-4" />}
      {live ? 'Live now' : next ? 'Next service' : 'Watch services'}
    </Link>
  );
};

export default WatchLiveLink;
